import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicService } from '../services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Phone, Mail, MapPin, Calendar, Gauge, Fuel, Settings, Car as CarIcon } from 'lucide-react';

const PublicCarDetailPage = () => {
  const { slug, carId } = useParams();
  const [car, setCar] = useState(null);
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    loadData();
  }, [slug, carId]);

  const loadData = async () => {
    try {
      const [carData, agencyData] = await Promise.all([
        publicService.getCarDetail(slug, carId),
        publicService.getAgencyBySlug(slug)
      ]);
      setCar(carData);
      setAgency(agencyData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando...</div>;
  }

  if (!car || !agency) {
    return <div className="min-h-screen flex items-center justify-center">Vehículo no encontrado</div>;
  }

  const whatsappMessage = `Hola, estoy interesado en el ${car.brand} ${car.model} ${car.year}`;
  const whatsappUrl = `https://wa.me/${agency.contacts?.whatsapp}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-slate-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Link to={`/a/${slug}/autos`} className="flex items-center gap-2 text-slate-300 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Volver al catálogo
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {agency.name}
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="mb-6">
              {car.business_status === 'SOLD' && (
                <Badge className="bg-red-600 text-white text-lg px-4 py-2 mb-4">
                  VENDIDO
                </Badge>
              )}
              {car.business_status === 'RESERVED' && (
                <Badge className="bg-yellow-600 text-white text-lg px-4 py-2 mb-4">
                  RESERVADO
                </Badge>
              )}
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {car.brand} {car.model}
              </h1>
              <p className="text-xl text-slate-600">{car.year}</p>
            </div>

            <div className="mb-6">
              {car.photos.length > 0 ? (
                <div>
                  <div className="rounded-xl overflow-hidden mb-4">
                    <img
                      src={car.photos[selectedImage]?.url || car.photos[0]?.url}
                      alt={`${car.brand} ${car.model}`}
                      className="w-full h-96 object-cover"
                    />
                  </div>
                  {car.photos.length > 1 && (
                    <div className="grid grid-cols-5 gap-2">
                      {car.photos.map((photo, index) => (
                        <div
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          className={`cursor-pointer rounded-lg overflow-hidden border-2 ${
                            selectedImage === index ? 'border-blue-600' : 'border-transparent'
                          }`}
                        >
                          <img
                            src={photo.thumb_url}
                            alt={`Vista ${index + 1}`}
                            className="w-full h-20 object-cover"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-96 bg-slate-200 rounded-xl flex items-center justify-center">
                  <CarIcon className="w-24 h-24 text-slate-400" />
                </div>
              )}
            </div>

            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Especificaciones
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-slate-500">Año</p>
                      <p className="font-bold text-slate-900">{car.year}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Gauge className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-slate-500">Kilómetros</p>
                      <p className="font-bold text-slate-900">{car.km.toLocaleString()} km</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Fuel className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-slate-500">Motor</p>
                      <p className="font-bold text-slate-900">{car.engine_type}</p>
                      {car.engine_details && (
                        <p className="text-sm text-slate-600">{car.engine_details}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Settings className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-slate-500">Transmisión</p>
                      <p className="font-bold text-slate-900">
                        {car.transmission === 'MANUAL' ? 'Manual' : 'Automático'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CarIcon className="w-5 h-5 text-blue-600 mt-1" />
                    <div>
                      <p className="text-sm text-slate-500">Condición</p>
                      <p className="font-bold text-slate-900">
                        {car.condition === 'NEW' ? 'Nuevo' : 'Usado'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {car.details && (
              <Card className="mb-6">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Descripción
                  </h2>
                  <p className="text-slate-700 whitespace-pre-line">{car.details}</p>
                </CardContent>
              </Card>
            )}

            {car.features && car.features.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Características
                  </h2>
                  <div className="grid grid-cols-2 gap-3">
                    {car.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-600 rounded-full" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <p className="text-sm text-slate-500 mb-2">Precio</p>
                    <p className="text-4xl font-bold text-blue-600">
                      {car.price.currency} ${car.price.amount.toLocaleString()}
                    </p>
                  </div>

                  {agency.contacts?.whatsapp && car.business_status === 'AVAILABLE' && (
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block mb-4">
                      <Button className="w-full bg-green-600 hover:bg-green-700" size="lg">
                        <Phone className="w-5 h-5 mr-2" />
                        Consultar por WhatsApp
                      </Button>
                    </a>
                  )}

                  {car.business_status !== 'AVAILABLE' && (
                    <div className="bg-slate-100 text-slate-600 text-center py-3 px-4 rounded-lg mb-4">
                      Este vehículo no está disponible
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-slate-900 mb-4">Datos del Vendedor</h3>
                  <div className="space-y-3">
                    <p className="font-bold text-slate-900">{agency.name}</p>
                    {agency.contacts?.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4" />
                        <span>{agency.contacts.phone}</span>
                      </div>
                    )}
                    {agency.contacts?.email && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4" />
                        <span>{agency.contacts.email}</span>
                      </div>
                    )}
                    {agency.location?.city && (
                      <div className="flex items-start gap-2 text-slate-600">
                        <MapPin className="w-4 h-4 mt-1" />
                        <div>
                          {agency.location.address && <div>{agency.location.address}</div>}
                          <div>{agency.location.city}, {agency.location.province}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCarDetailPage;